import {
  AttachmentMeta,
  MessageContentEntity,
  VoiceAttachmentMeta,
} from "@/indexedDB/types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

const AVATAR_BUCKET_URL = import.meta.env.VITE_AVATAR_BUCKET_URL;
let hasWarnedMissingAvatarBucketUrl = false;

const ATTACHMENTS_BUCKET_URL = import.meta.env.VITE_ATTACHMENTS_BUCKET_URL;
let hasWarnedMissingAttachmentsBucketUrl = false;

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatMessageTimestamp(input: number | string | Date): string {
  let date: Date;

  if (typeof input === "number") {
    date = new Date(input);
  } else if (typeof input === "string") {
    date = new Date(input);
  } else if (input instanceof Date) {
    date = input;
  } else {
    throw new Error(
      "Invalid input type. Expected number, string, or Date object.",
    );
  }

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date input.");
  }

  let hours = date.getHours();
  const minutes = date.getMinutes();
  const amOrPm = hours >= 12 ? "PM" : "AM";

  hours = hours % 12 || 12; // Convert to 12-hour format, making 0 into 12

  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes; // Ensure two digits for minutes

  return `${hours}:${formattedMinutes} ${amOrPm}`;
}

export function convertToMilliseconds(timeString: string): number {
  // Use the Date constructor to parse the string and get the timestamp in milliseconds
  const date = new Date(timeString);
  if (isNaN(date.getTime())) {
    return 0;
  }
  return date.getTime();
}

export function formatRecordingDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const centiseconds = Math.floor((ms % 1000) / 10);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  const mm = String(mins).padStart(2, "0");
  const ss = String(secs).padStart(2, "0");
  const cc = String(centiseconds).padStart(2, "0");

  return `${mm}:${ss}.${cc}`;
}

export function secondsToMMSS(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secondsRemaining = seconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(secondsRemaining).padStart(2, "0")}`;
}

export function processAvatarBucketUrl(data: string) {
  if (!data || data === "first-user-profile.img") {
    return "";
  }
  // Already a full URL (legacy data)
  if (data.includes("://")) {
    return data;
  }
  if (!AVATAR_BUCKET_URL) {
    if (!hasWarnedMissingAvatarBucketUrl) {
      console.warn("VITE_AVATAR_BUCKET_URL is not configured");
      hasWarnedMissingAvatarBucketUrl = true;
    }
    return "";
  }
  return AVATAR_BUCKET_URL.replace(":imageId", data);
}

export function processAttachmentBucketUrl(data: string) {
  // If there is no data, return empty string
  if (!data) {
    return "";
  }

  // Return the image url from the server
  if (!ATTACHMENTS_BUCKET_URL) {
    if (!hasWarnedMissingAttachmentsBucketUrl) {
      console.warn("VITE_ATTACHMENTS_BUCKET_URL is not configured");
      hasWarnedMissingAttachmentsBucketUrl = true;
    }
    return "";
  }
  return ATTACHMENTS_BUCKET_URL.replace(":attachmentId", data);
}

/**
 * Formats a timestamp for display in chat list entries.
 *
 * Returns:
 * - Hour format if message is from today
 * - Day of week if message is within the past 7 days
 * - Date format if message is older
 */
export const formatChatListEntryTimestamp = (
  timestamp: string | number | Date,
): string => {
  const messageDate = new Date(timestamp);
  const now = new Date();

  // Check if the message is from today
  const isToday: boolean = messageDate.toDateString() === now.toDateString();

  if (isToday) {
    // Display time in HH:mm format
    return formatMessageTimestamp(messageDate);
  }

  // Calculate date 7 days ago
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(now.getDate() - 7);
  sevenDaysAgo.setHours(0, 0, 0, 0); // Start of the day

  // Check if the message is within the past 7 days
  const isWithinPastWeek: boolean = messageDate >= sevenDaysAgo;

  if (isWithinPastWeek) {
    // Display day of the week in short format (first 3 letters, e.g., "Mon")
    return messageDate.toLocaleDateString([], { weekday: "short" });
  } else {
    // Detect user locale using navigator.language or default to undefined
    const userLocale =
      typeof navigator !== "undefined" ? navigator.language : undefined;

    // Display date in a locale-based format (DD.MM.YY)
    const output = messageDate
      .toLocaleDateString(userLocale, {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit", // Short year (YY format)
      })
      .replace(/\//g, "."); // Replace slashes with dots

    return output;
  }
};

export function stringToRgbColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const r = (hash >> 16) & 0xff;
  const g = (hash >> 8) & 0xff;
  const b = hash & 0xff;

  return `${r}, ${g}, ${b}`; // Return RGB values
}

export function stringToHexColor(input: string): string {
  // Hash function: Generate a numeric hash from the input string
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = input.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Use the hash to derive HSL values
  const hue = Math.abs(hash % 360); // Hue ranges from 0 to 359
  const saturation = 70; // Saturation is fixed for vibrant colors
  const lightness = 50; // Lightness is fixed for balance between dark/light

  // Convert HSL to hex
  return hslToHex(hue, saturation, lightness);
}

export function hslToHex(h: number, s: number, l: number): string {
  const saturation = s / 100;
  const lightness = l / 100;

  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - chroma / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h >= 0 && h < 60) {
    r = chroma;
    g = x;
    b = 0;
  } else if (h >= 60 && h < 120) {
    r = x;
    g = chroma;
    b = 0;
  } else if (h >= 120 && h < 180) {
    r = 0;
    g = chroma;
    b = x;
  } else if (h >= 180 && h < 240) {
    r = 0;
    g = x;
    b = chroma;
  } else if (h >= 240 && h < 300) {
    r = x;
    g = 0;
    b = chroma;
  } else if (h >= 300 && h < 360) {
    r = chroma;
    g = 0;
    b = x;
  }

  // Convert to RGB values [0-255], then to hex
  r = Math.round((r + m) * 255);
  g = Math.round((g + m) * 255);
  b = Math.round((b + m) * 255);

  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

export function getAttachmentCategory(
  meta: AttachmentMeta | VoiceAttachmentMeta,
): keyof Omit<MessageContentEntity, "text" | "link"> {
  if (meta?.fileType.startsWith("image/")) {
    return "media";
  } else if (meta?.fileType.startsWith("video/")) {
    return "media";
  } else if (meta?.fileType.startsWith("audio/")) {
    return "voice";
  } else {
    return "file";
  }
}

export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) {
    return "0 Bytes";
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));
  return `${size} ${sizes[i]}`;
}

export function removeEmptyTags(html: string): string {
  // 1. Parse the HTML into a Document.
  const parser = new DOMParser();
  const doc: Document = parser.parseFromString(html, "text/html");

  // 2. Create a TreeWalker over element nodes only.
  const walker: TreeWalker = doc.createTreeWalker(
    doc.body, // root node
    NodeFilter.SHOW_ELEMENT, // show only Element nodes
    {
      acceptNode(node: Node): number {
        const el = node as Element;
        // Accept if no non-whitespace text and no child elements.
        return !el.textContent?.trim() && el.childElementCount === 0
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    },
  );

  // 3. Collect and remove all empty nodes.
  const empties: Element[] = [];
  while (walker.nextNode()) {
    // advance through matched nodes
    empties.push(walker.currentNode as Element);
  }
  empties.forEach((el) => el.remove()); // remove from DOM

  // 4. Serialize the cleaned document back to HTML.
  return doc.body.innerHTML; // returns cleaned HTML
}

export function isValidLink(link: string | undefined): boolean {
  if (!link) {
    return false;
  }
  if (link.includes("mailto:")) {
    return false;
  }
  if (link.includes("tel:")) {
    return false;
  }
  try {
    new URL(link);
    return true;
  } catch {
    return false;
  }
}

const textarea = document.createElement("textarea");
export function decodeHTMLEntities(rawHTML: string) {
  textarea.innerHTML = rawHTML;
  return textarea.value;
}

export function timeDiffFromNow(
  input: string | Date | number,
  unit: "ms" | "seconds" | "minutes" | "hours" | "days",
): number | null {
  const targetDate = new Date(input);
  if (isNaN(targetDate.getTime())) {
    return null;
  }

  const now = Date.now();
  const diffMs = Math.abs(now - targetDate.getTime());

  const values = {
    diffMs: Math.round(diffMs),
    diffSeconds: Math.round(diffMs / 1000),
    diffMinutes: Math.round(diffMs / (1000 * 60)),
    diffHours: Math.round(diffMs / (1000 * 60 * 60)),
    diffDays: Math.round(diffMs / (1000 * 60 * 60 * 24)),
  };

  switch (unit) {
    case "ms":
      return values.diffMs;
    case "seconds":
      return values.diffSeconds;
    case "minutes":
      return values.diffMinutes;
    case "hours":
      return values.diffHours;
    case "days":
      return values.diffDays;
    default:
      return null;
  }
}

/**
 * Linearly interpolates between two numeric values.
 *
 * Given a start value `a` and an end value `b`, returns the value that lies
 * `t` fraction of the way from `a` to `b`. The interpolation factor `t` is
 * typically in the range [0, 1], where 0 returns `a` and 1 returns `b`.
 * No clamping is performed on `t` (values outside [0, 1] will extrapolate).
 *
 * @param a - Start value.
 * @param b - End value.
 * @param t - Interpolation factor between 0 and 1 (no automatic clamping).
 * @returns The interpolated numeric value at fraction `t` between `a` and `b`.
 *
 * @example
 * lerp(0, 10, 0.5); // returns 5
 */
export const lerp = (a: number, b: number, t: number): number => {
  return a + t * (b - a);
};

/**
 * Convert a hexadecimal color to an rgba(...) CSS string.
 *
 * The `hex` parameter may optionally start with a leading '#'. This function
 * expects a 6-digit hexadecimal color (e.g. "#aabbcc" or "aabbcc"). The
 * returned string is a valid CSS `rgba(r, g, b, a)` representation where
 * `r`, `g`, and `b` are integer values in [0, 255] and `a` is the supplied
 * alpha value.
 *
 * Note: This function does not perform strict validation on the `hex` input.
 * Passing non-hex characters or an incorrectly sized hex string may produce
 * unexpected results.
 *
 * @param hex - Hex color string (with or without leading '#'), expected 6 hex digits.
 * @param alpha - Opacity value between 0 and 1 (default: 1).
 * @returns A CSS rgba(...) string representing the color and alpha.
 *
 * @example
 * hexToRgba('#ff0000', 0.5); // returns "rgba(255, 0, 0, 0.5)"
 */
export const hexToRgba = (hex: string, alpha: number = 1): string => {
  if (hex.startsWith("#")) {
    hex = hex.slice(1);
  }

  const bigint = Number.parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  // Defer cleanup: revoking the object URL (or detaching the anchor) in the
  // same tick can abort the click-initiated download before the browser has
  // started reading the blob — observed as empty/failed saves in Firefox and
  // some Safari versions. Let the current task settle, then release.
  setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 1000);
}
